/**
 * PhilosophyIcon.tsx — inline SVG map for the 10 approved philosophy icons.
 *
 * Architecture rule (kit §12 §4 v1.4): inline SVG only. Never <img src="*.svg">.
 * The two-color strokes (#2A2233 dark ink + #D4B86E gold accent) are hardcoded
 * in the SVG path attributes. Inline SVG keeps the paths in the DOM for consistent
 * rendering; <img> would isolate them from the page cascade (though colors are
 * hardcoded, not currentColor, so the rendering would be identical — inline is the
 * canonical documented pattern matching existing BlogPostPageClient usage).
 *
 * Source files: Brand/BrandAssets/icons/philosophy-set-v1/
 * Approved working set for the 12 video-blog migrations (2026-05-09).
 * Any icon outside this set requires Aria sign-off before use.
 *
 * Usage:
 *   <PhilosophyIcon name="preparation-before-sound" width={48} height={48} />
 *   <PhilosophyIcon name="slow-practice-depth" width={20} height={20} />
 *
 * All icons have viewBox="0 0 64 64" and scale correctly from any width/height.
 */

export type PhilosophyIconName =
  | 'preparation-before-sound'
  | 'listen-before-play'
  | 'slow-practice-depth'
  | 'honest-practice'
  | 'discipline-curiosity'
  | 'mistakes-as-data'
  | 'inner-pulse'
  | 'technique-serves-expression'
  | 'multi-tempo'
  | 'wrist-freedom'
  | 'performer-interpretation'
  | 'self-critical-kindness';

interface PhilosophyIconProps {
  name: PhilosophyIconName;
  width?: number;
  height?: number;
  className?: string;
}

export default function PhilosophyIcon({ name, width = 48, height = 48, className }: PhilosophyIconProps) {
  const paths = ICON_PATHS[name];
  if (!paths) return null;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable={false}
      width={width}
      height={height}
      className={className}
    >
      {paths}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Icon path data — extracted verbatim from Brand/BrandAssets/icons/philosophy-set-v1/
// ---------------------------------------------------------------------------
const ICON_PATHS: Record<PhilosophyIconName, React.ReactNode> = {

  'preparation-before-sound': (
    <>
      {/* Dark vertical stem */}
      <path d="M22 12 L22 52" stroke="#2A2233" strokeWidth="2"/>
      {/* Dark P-curve from stem */}
      <path d="M22 12 L34 12 A10 10 0 0 1 34 32 L22 32" stroke="#2A2233" strokeWidth="2"/>
      {/* Gold notehead (filled ellipse) */}
      <ellipse cx="44" cy="17" rx="2.6" ry="2" fill="#D4B86E" stroke="none" transform="rotate(-20 44 17)"/>
      {/* Gold note stem upward */}
      <path d="M46.4 16.3 L46.4 8" stroke="#D4B86E" strokeWidth="1.3" strokeLinecap="round"/>
      {/* Gold flag curve */}
      <path d="M46.4 8 C48.6 9 49.6 10.8 48.6 12.4" stroke="#D4B86E" strokeWidth="1.3" strokeLinecap="round" fill="none"/>
    </>
  ),

  'listen-before-play': (
    <>
      {/* Ear outer arc (dark) — shifted right */}
      <path d="M46 13 C34 11 26 19 26 30 C26 39 31 46 38 47.5 C42 48.2 45 46 45 42.5" stroke="#2A2233" strokeWidth="1.9"/>
      {/* Ear inner helix (dark) */}
      <path d="M42 20 C35 20 32 25 33 31 C34 36 37 39 41 39" stroke="#2A2233" strokeWidth="1.7"/>
      {/* Canal inner curve (dark) */}
      <path d="M41 31 C42.5 31 43 32.8 41.8 34" stroke="#2A2233" strokeWidth="1.5"/>
      {/* Gold notehead arriving from left toward ear opening */}
      <ellipse cx="16" cy="30" rx="3" ry="2.2" fill="#D4B86E" stroke="none" transform="rotate(-15 16 30)"/>
      {/* Gold note stem */}
      <path d="M18.8 29.2 L18.8 21.5" stroke="#D4B86E" strokeWidth="1.4"/>
      {/* Gold motion arc — note moving rightward toward ear */}
      <path d="M20 30 C23 30 25 30 27 30" stroke="#D4B86E" strokeWidth="1.3" strokeDasharray="1.5 2"/>
    </>
  ),

  'slow-practice-depth': (
    <>
      {/* Innermost arc ring */}
      <path d="M32 20 C36.5 20 40 24.5 40 29 C40 33.5 36.5 37.5 32 37.5 C27.5 37.5 24 33.5 24 29 C24 24.5 27.5 20 32 20" stroke="#2A2233" strokeWidth="1.8"/>
      {/* Middle arc ring */}
      <path d="M32 13 C40 13 47 19.5 47 29 C47 38.5 40 45 32 45 C24 45 17 38.5 17 29 C17 19.5 24 13 32 13" stroke="#2A2233" strokeWidth="1.7"/>
      {/* Outer arc ring */}
      <path d="M32 7 C43 7 54 16 54 29 C54 42 43 53 32 53 C21 53 10 42 10 29 C10 16 21 7 32 7" stroke="#2A2233" strokeWidth="1.6"/>
      {/* Gold center dot — the focal depth point */}
      <ellipse cx="32" cy="29" rx="2.5" ry="2.5" fill="#D4B86E" stroke="none"/>
    </>
  ),

  'honest-practice': (
    <>
      {/* Ear outer arc (dark, left side, faces right) */}
      <path d="M26 16 C16 16 10 22 10 30 C10 37 14 43 20 44.5 C23 45 26 43.5 26 41" stroke="#2A2233" strokeWidth="1.8"/>
      {/* Ear inner helix (dark) */}
      <path d="M22 22 C16 23 14 27 15 31 C16 35 18 38 21 38" stroke="#2A2233" strokeWidth="1.6"/>
      {/* Ear inner canal gesture (dark) */}
      <path d="M21 31 C22 30 23 31 22.5 32.5" stroke="#2A2233" strokeWidth="1.4"/>
      {/* Mirror vertical line (dark) */}
      <path d="M40 14 L40 50" stroke="#2A2233" strokeWidth="1.7"/>
      {/* Mirror top and bottom serifs */}
      <path d="M36 14 L44 14" stroke="#2A2233" strokeWidth="1.5"/>
      <path d="M36 50 L44 50" stroke="#2A2233" strokeWidth="1.5"/>
      {/* Returning signal arc — from mirror back to ear (dark, lighter) */}
      <path d="M40 30 C36 30 32 30 28 30" stroke="#2A2233" strokeWidth="1.2" strokeDasharray="1.5 2.5"/>
      {/* Gold focal dot on mirror surface — truth point */}
      <ellipse cx="40" cy="30" rx="2.2" ry="2.2" fill="#D4B86E" stroke="none"/>
    </>
  ),

  'discipline-curiosity': (
    <>
      {/* Dark vertical line — discipline */}
      <path d="M32 8 L32 56" stroke="#2A2233" strokeWidth="1.9"/>
      {/* Dark circular loop — curiosity */}
      <path d="M32 14 C42 14 52 22 52 32 C52 42 42 50 32 50 C22 50 12 42 12 32 C12 22 22 14 32 14" stroke="#2A2233" strokeWidth="1.7" fill="none"/>
      {/* Gold dot at the intersection (center) */}
      <ellipse cx="32" cy="32" rx="2.8" ry="2.8" fill="#D4B86E" stroke="none"/>
    </>
  ),

  'mistakes-as-data': (
    <>
      {/* Dark X mark (mistake symbol) — two crossing bezier diagonals */}
      <path d="M14 20 C16.5 23 19 26.5 22 30 C19 33.5 16.5 37 14 40" stroke="#2A2233" strokeWidth="1.9"/>
      <path d="M22 20 C19.5 23 17 26.5 14 30 C17 33.5 19.5 37 22 40" stroke="#2A2233" strokeWidth="1.9"/>
      {/* Gold magnifying glass circle */}
      <path d="M38 18 C44.5 18 50 23.5 50 30 C50 36.5 44.5 42 38 42 C31.5 42 26 36.5 26 30 C26 23.5 31.5 18 38 18" stroke="#D4B86E" strokeWidth="1.6" fill="none"/>
      {/* Gold magnifying glass handle */}
      <path d="M46 39 L54 48" stroke="#D4B86E" strokeWidth="1.7"/>
    </>
  ),

  'inner-pulse': (
    <>
      {/* Heart silhouette (dark, two lobes + pointed base) */}
      <path d="M32 52 C30 49 14 38 11 28 C8.5 19 14 11 22 11 C26.5 11 30 13.5 32 16.5 C34 13.5 37.5 11 42 11 C50 11 55.5 19 53 28 C50 38 34 49 32 52" stroke="#2A2233" strokeWidth="1.9" fill="none"/>
      {/* Gold pendulum line inside heart — the inner pulse axis */}
      <path d="M32 17 L32 46" stroke="#D4B86E" strokeWidth="1.6"/>
      {/* Small gold pendulum bob at bottom of line */}
      <ellipse cx="32" cy="46" rx="2.2" ry="2.2" fill="#D4B86E" stroke="none"/>
    </>
  ),

  'technique-serves-expression': (
    <>
      {/* Palm mass — top-down rounded rectangle */}
      <path d="M20 34 C19 30 20 26 24 24 C26 23 30 22.5 32 22.5 C34 22.5 38 23 40 24 C44 26 45 30 44 34 C43 38 40 42 32 42 C24 42 21 38 20 34 Z" stroke="#2A2233" strokeWidth="1.8"/>
      {/* Thumb — left side */}
      <path d="M20 32 C17 31 15 28 16 25 C17 23 19 23 20 24.5" stroke="#2A2233" strokeWidth="1.6"/>
      {/* Index finger */}
      <path d="M27 22.5 C27 20 27.5 17.5 28.5 16 C29.5 14.5 31 14.5 31 16 C31 18 31 21 31 22.5" stroke="#2A2233" strokeWidth="1.7"/>
      {/* Middle finger */}
      <path d="M31 22.5 C31 19.5 32 17 33 15.5 C34 14 35.5 14 35.5 15.5 C35.5 17.5 35.5 20.5 35.5 22.5" stroke="#2A2233" strokeWidth="1.7"/>
      {/* Ring finger */}
      <path d="M35.5 22.5 C35.5 20 36 18 37 17 C38 16 39 16.5 39 18 C39 20 38.5 22 38.5 22.5" stroke="#2A2233" strokeWidth="1.6"/>
      {/* Pinky */}
      <path d="M38.5 23 C38.5 21 39 19.5 40 19 C41 18.5 42 19.5 41.5 21 C41 22 41 23 41 23" stroke="#2A2233" strokeWidth="1.5"/>
      {/* Gold phrase arc — musical slur above the fingertips, the expression */}
      <path d="M24 11 C28 7.5 36 7.5 40 11" stroke="#D4B86E" strokeWidth="1.6"/>
    </>
  ),

  'multi-tempo': (
    <>
      {/* Left dark eighth note — faster feel */}
      <path d="M13 46 C12 43.5 14.5 41.5 17 42 C19.5 42.5 20.5 45 19.5 47 C18.5 49 16 49.5 14 48.5 C12.8 47.8 13 46.5 13 46" stroke="#2A2233" strokeWidth="1.6" fill="#2A2233"/>
      <path d="M19.5 45 L19.5 27" stroke="#2A2233" strokeWidth="1.6"/>
      <path d="M19.5 27 C24 29 25 33 22 36" stroke="#2A2233" strokeWidth="1.5" fill="none"/>
      {/* Center gold eighth note — focal accent */}
      <ellipse cx="33" cy="44" rx="4" ry="3" fill="#D4B86E" stroke="none" transform="rotate(-15 33 44)"/>
      <path d="M36 42 L36 21" stroke="#D4B86E" strokeWidth="1.6"/>
      <path d="M36 21 C41 23.5 42.5 28 38.5 32" stroke="#D4B86E" strokeWidth="1.5" fill="none"/>
      {/* Right dark eighth note — slowest feel */}
      <path d="M46 46 C45 43.5 47.5 41.5 50 42 C52.5 42.5 53.5 45 52.5 47 C51.5 49 49 49.5 47 48.5 C45.8 47.8 46 46.5 46 46" stroke="#2A2233" strokeWidth="1.6" fill="#2A2233"/>
      <path d="M52.5 45 L52.5 24" stroke="#2A2233" strokeWidth="1.6"/>
      <path d="M52.5 24 C57 26.5 58 31 55 34" stroke="#2A2233" strokeWidth="1.5" fill="none"/>
    </>
  ),

  'wrist-freedom': (
    <>
      {/* Forearm upper edge */}
      <path d="M6 29 C10 28.5 17 28.5 22 29" stroke="#2A2233" strokeWidth="1.9"/>
      {/* Forearm lower edge */}
      <path d="M6 34 C10 34 17 34 22 33.5" stroke="#2A2233" strokeWidth="1.9"/>
      {/* Wrist joint — upper */}
      <path d="M22 29 C24 28.5 26.5 28 27.5 26.5 C28.5 25 28.5 23.5 27 23" stroke="#2A2233" strokeWidth="1.8"/>
      <path d="M27 23 C28.5 23 30 24 30 25.5" stroke="#2A2233" strokeWidth="1.7"/>
      {/* Wrist joint — lower */}
      <path d="M22 33.5 C24 34 26.5 34.5 27.5 36 C28.5 37.5 28.5 39 27 39.5" stroke="#2A2233" strokeWidth="1.8"/>
      <path d="M27 39.5 C28.5 39.5 30 38.5 30 37" stroke="#2A2233" strokeWidth="1.7"/>
      {/* Hand body */}
      <path d="M30 25.5 C33 25.5 37 26 39 27 C41 28 42 29.5 42 31 C42 32.5 41 34 39 34.5 C37 35 33 35 30 37" stroke="#2A2233" strokeWidth="1.7"/>
      {/* Index finger */}
      <path d="M39 27 C42 25.5 46 24.5 49 24.5 C51 24.5 52 25.5 51 27 C50 28 47 28.5 44 28.5" stroke="#2A2233" strokeWidth="1.5"/>
      {/* Middle finger */}
      <path d="M40 28.5 C43 27 47 26.5 50 27 C52 27.5 52 29 50 29.5 C48 30 45 30 42 30" stroke="#2A2233" strokeWidth="1.5"/>
      {/* Ring finger */}
      <path d="M40 30.5 C43 30 46 30 48 30.5 C50 31 50 32.5 48 33 C46 33.5 43 33.5 41 33" stroke="#2A2233" strokeWidth="1.4"/>
      {/* Gold encircling orbit arrow */}
      <path d="M22 37 C18 35 15 31 17 27 C19 23 24 21 28 21 C32 21 36 23 37 27 C38 31 36 36 33 38 C31 39.5 28 40 25 39" stroke="#D4B86E" strokeWidth="1.6"/>
      {/* Arrowhead */}
      <path d="M25 39 L22 41 M25 39 L23 37" stroke="#D4B86E" strokeWidth="1.4"/>
    </>
  ),

  // ---------------------------------------------------------------------------
  // Added 2026-05-09 (pfm-43-44 fingering post)
  // Source: Brand/BrandAssets/icons/philosophy-set-v1/icon-philosophy-performer-interpretation.svg
  // ---------------------------------------------------------------------------
  'performer-interpretation': (
    <>
      {/* Mask outer oval — slightly tilted clockwise */}
      <path d="M14 20 C12 24 11 30 13 36 C15 42 21 46 27 45 C33 44 37 39 38 33 C39 27 37 21 33 17 C29 13 23 13 19 15 C16 17 14.5 18.5 14 20 Z"
        stroke="#2A2233" strokeWidth="1.8"/>
      {/* Left eye hole */}
      <path d="M19 26 C18 27.5 18 29.5 19 31 C20 32.5 22 32.5 23 31 C24 29.5 24 27.5 23 26 C22 24.5 20 24.5 19 26 Z"
        stroke="#2A2233" strokeWidth="1.4"/>
      {/* Right eye hole */}
      <path d="M27 24 C26 25.5 26 27.5 27 29 C28 30.5 30 30.5 31 29 C32 27.5 32 25.5 31 24 C30 22.5 28 22.5 27 24 Z"
        stroke="#2A2233" strokeWidth="1.4"/>
      {/* Curved smile */}
      <path d="M19 37 C22 40 28 40 31 37" stroke="#2A2233" strokeWidth="1.6"/>
      {/* Gold filled notehead to the right */}
      <ellipse cx="49" cy="34" rx="5" ry="3.5" fill="#D4B86E" stroke="none" transform="rotate(-15 49 34)"/>
      {/* Note stem upward */}
      <path d="M53 32 L53 18" stroke="#D4B86E" strokeWidth="1.5"/>
    </>
  ),

  // ---------------------------------------------------------------------------
  // Added 2026-05-09 (pfm-43-44 fingering post)
  // Source: Brand/BrandAssets/icons/philosophy-set-v1/icon-philosophy-self-critical-kindness.svg
  // ---------------------------------------------------------------------------
  'self-critical-kindness': (
    <>
      {/* Dark eye — almond outer shape upper arc */}
      <path d="M9 31 C16 22 30 20 38 25 C34 30 26 35 14 36 C12 35 9 33 9 31"
        stroke="#2A2233" strokeWidth="1.8" fill="none"/>
      {/* Dark eye — almond outer shape lower arc */}
      <path d="M9 31 C16 40 30 42 38 37 C34 32 26 27 14 26 C12 27 9 29 9 31"
        stroke="#2A2233" strokeWidth="1.8" fill="none"/>
      {/* Pupil circle */}
      <path d="M24 26.5 C27.5 26.5 30.5 28.5 30.5 31 C30.5 33.5 27.5 35.5 24 35.5 C20.5 35.5 17.5 33.5 17.5 31 C17.5 28.5 20.5 26.5 24 26.5"
        stroke="#2A2233" strokeWidth="1.6" fill="none"/>
      {/* Gold small heart — kindness toward the self-observer */}
      <path d="M47 26 C46 24 44 24 43 25.5 C42 24 40 24 39 26 C39 28.5 43 33 43 33 C43 33 47 28.5 47 26"
        stroke="#D4B86E" strokeWidth="1.5" fill="#D4B86E"/>
    </>
  ),
};
